(ns hai.entity.form
  "The composer chrome the note and task forms share: the Save / Cancel footer,
   and the save shortcut the Save carries as key caps."
  (:require
   [hai.ui.icons :as icons]
   [hai.ui.keyboard :as keyboard]
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

(defn chord
  "The platform chord for `shortcut-key`, key by key — `[\"⌘\" \"↵\"]` on a
   Mac, `[\"Ctrl\" \"↵\"]` elsewhere."
  [shortcut-key]
  [(if keyboard/mac? "⌘" "Ctrl") shortcut-key])

(defn shortcut-keys
  "A shortcut's keys as key caps, for a Save to carry beside its label."
  [shortcut]
  (into [:span.shortcut-keys]
        (for [key-label shortcut]
          [:kbd key-label])))

;; Cancel beside Save, right-aligned; whatever a form puts in `.footer-start`
;; sits at the far left. The Save's key caps sit on a translucent fill so they
;; read as keys rather than as more label; while a save is in flight the
;; spinner takes their slot and the button holds most of its colour.
(o/defstyled footer :div
  {:margin-top "14px"
   :display "flex" :align-items "center" :justify-content "flex-end"
   ;; A question is longer than the actions it stands in for, and a narrow
   ;; surface — the mobile pad — can't fit it on one line with its answers.
   ;; Let it take a line of its own rather than squeezing them.
   :flex-wrap "wrap"
   :column-gap "14px" :row-gap "8px"}
  [:.footer-start
   {:margin-right "auto"
    :display "flex" :align-items "center" :gap "10px"
    :min-width "0"}]
  [:.form-cancel
   {:border "none" :background "transparent"
    :font-family "inherit"
    :font-size "13px" :font-weight "600"
    :color t/--link-muted :cursor "pointer"}
   [:&:hover {:color t/--card-forest}]
   [:&:disabled {:opacity "0.35" :cursor "not-allowed"}]
   [:&:disabled:hover {:color t/--link-muted}]]
  ;; Sits with the two buttons it belongs to, at the footer's right — only
  ;; `.footer-start` claims the space to their left.
  [:.confirm-prompt
   {:font-size "13px" :font-weight "600" :color t/--note-kraft}]
  ;; The answers stay on one line each — it is the question that wraps.
  [#{:.form-cancel :.form-save :.form-discard} {:white-space "nowrap"}]
  ;; The Save's shape in the destructive colour, so swapping one for the other
  ;; doesn't move anything.
  [:.form-discard
   {:display "inline-flex" :align-items "center"
    :padding "7px 18px"
    :border "none" :border-radius "8px"
    :background t/--card-error :color "white"
    :font-family "inherit"
    :font-size "13px" :font-weight "600"
    :cursor "pointer"
    :transition "background 0.15s ease"}
   [:&:hover {:background "#7D2020"}]]
  [:.form-save
   {:display "inline-flex" :align-items "center" :gap "8px"
    :padding "7px 18px"
    :border "none" :border-radius "8px"
    :background t/--humble-primary :color "white"
    :font-family "inherit"
    :font-size "13px" :font-weight "600"
    :cursor "pointer"
    :transition "background 0.15s ease, opacity 0.15s ease"}
   [:&:hover {:background t/--humble-primary-dark}]
   [:&:disabled {:opacity "0.35" :cursor "not-allowed"}]
   [:&.saving:disabled {:opacity "0.75" :cursor "progress"}]
   ;; The key caps and the spinner carry their own weight at the right edge,
   ;; so they sit closer to it than the label does to the left — a Save with
   ;; neither (no hint on touch) stays symmetric.
   [icons/spinner
    {:margin-right "-6px"}
    [:path {:fill "white"}]]
   [:.shortcut-keys
    {:display "inline-flex" :align-items "center" :gap "3px"
     :margin-right "-6px"}]
   [:kbd
    {:font-family "inherit" :font-size "11px" :font-weight "600" :line-height "1"
     :min-width "18px" :box-sizing "border-box" :text-align "center"
     :padding "3px 5px"
     :border-radius "4px"
     :background "rgba(255,255,255,0.18)"
     :box-shadow "inset 0 -1px 0 rgba(0,0,0,0.25)"}]])

(defn confirm
  "A question in the footer, standing where the actions were: the prompt and
   its two answers. `destructive?` colours the confirming one as a loss."
  [{:keys [prompt dismiss-label confirm-label destructive? on-dismiss on-confirm]}]
  [:<>
   [:span.confirm-prompt prompt]
   [:button.form-cancel
    {:type "button"
     :on-mouse-down #(.preventDefault %)
     :on-click on-dismiss}
    dismiss-label]
   [:button
    {:class (if destructive? "form-discard" "form-save")
     :type "button"
     :on-mouse-down #(.preventDefault %)
     :on-click on-confirm}
    confirm-label]])

(defn actions
  "The footer's Cancel and Save. Both are always there, and both disable while
   the composer holds nothing the entity as filed doesn't — there is nothing to
   file and nothing to throw away. Cancel does throw away everything staged
   since the last save, so it asks first: the click swaps the pair for the
   question, the way the ⋮ menu's Delete does. `shortcut` is what the Save
   carries as key caps; nil for touch. `save-disabled?` is a reason of the
   host's own beyond there being nothing to file — a task with no title."
  [{:keys [unfiled? saving? save-disabled? shortcut on-cancel on-save]}]
  (r/with-let [!confirming? (r/atom false)]
    ;; A composer that has nothing staged has nothing to confirm: a save landing
    ;; mid-question retires it rather than leaving it hanging over a clean form.
    (if (and @!confirming? unfiled?)
      [confirm
       {:prompt        "Discard changes?"
        :dismiss-label "Keep editing"
        :confirm-label "Discard"
        :destructive?  true
        :on-dismiss    #(reset! !confirming? false)
        :on-confirm    (fn []
                         (reset! !confirming? false)
                         (on-cancel))}]
      [:<>
       [:button.form-cancel
        {:type "button"
         :disabled (not unfiled?)
         :on-mouse-down #(.preventDefault %)
         :on-click #(reset! !confirming? true)}
        "Cancel"]
       [:button.form-save
        {:type "button"
         :class (when saving? "saving")
         :disabled (or saving? save-disabled? (not unfiled?))
         ;; Keep the focus in the text through the click, so the pad reads on
         ;; for the next line.
         :on-mouse-down #(.preventDefault %)
         :on-click on-save}
        "Save"
        (cond
          saving?        [icons/spinner {:size 14}]
          (seq shortcut) [shortcut-keys shortcut])]])))
